import { Platform, type View } from 'react-native';
import type { RefObject } from 'react';

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type CaptureRecapShareImageOptions = {
  delayMs?: number;
  quality?: number;
};

/** Lazy-load react-native-view-shot; returns null if the native module is not available (e.g. Expo Go or missing link). */
function getCaptureRef(): typeof import('react-native-view-shot')['captureRef'] | null {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const viewShot = require('react-native-view-shot');
    return viewShot?.captureRef ?? null;
  } catch {
    return null;
  }
}

export const captureRecapShareImage = async (
  targetRef: RefObject<View | null>,
  options: CaptureRecapShareImageOptions = {},
): Promise<string> => {
  if (Platform.OS === 'web') {
    throw new Error('Image capture is not supported on web.');
  }
  if (!targetRef.current) {
    throw new Error('Recap image not ready yet');
  }

  const captureRef = getCaptureRef();
  if (!captureRef) {
    throw new Error('Image capture is not available in this build. Use text-only share.');
  }

  // Let remote image layers paint before capture.
  await wait(options.delayMs ?? 180);
  try {
    return await captureRef(targetRef.current, {
      format: 'jpg',
      quality: options.quality ?? 0.92,
      result: 'tmpfile',
      // Do not force dimensions; preserving native layout avoids stretched text in shared images.
    });
  } catch (e) {
    if (e && typeof (e as Error).message === 'string' && ((e as Error).message.includes('RNViewShot') || (e as Error).message.includes('could not be found'))) {
      throw new Error('Image capture is not available in this build. Use text-only share.');
    }
    throw e;
  }
};
